/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   AttentionFeed.tsx
 *     Feature         :   254-pipeline-monitoring-overhaul (US4)
 *     Description     :   The dashboard's LEAD element (FR-027a): the failing / at-risk /
 *                         dormant / newly-arrived pipelines, ranked by tier-weighted
 *                         significance, so the first thing a viewer sees is what is wrong
 *                         and what to do. Explicit all-clear state when nothing needs
 *                         attention. Kit + tokens.
 *     Constitution    :   Principle XLV, XXXVII
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-24
 * =========================================================================================
 */
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, Badge } from '@/components/ui';
import type { AttentionFeedItem } from '@shared/types/pipelineMonitoring';

const CONDITION_LABEL: Record<AttentionFeedItem['condition'], string> = {
  failing: 'failing', at_risk: 'at risk', dormant: 'dormant', new_arrival: 'new — needs triage',
};

function sevVariant(s: AttentionFeedItem['severity']): 'danger' | 'warning' | 'default' {
  return s === 'Critical' ? 'danger' : s === 'Warning' ? 'warning' : 'default';
}

export function AttentionFeed({ items }: { items: AttentionFeedItem[] }): JSX.Element {
  return (
    <Card title="Needs attention">
      {items.length === 0 ? (
        <div className="py-6 text-center text-sm text-[var(--color-text-secondary)]">
          Nothing needs attention — every monitored pipeline is healthy.
        </div>
      ) : (
        <ul className="divide-y divide-[var(--color-border)]">
          {items.map((it) => (
            <li key={`${it.appInstance}-${it.condition}`} className="flex items-center justify-between gap-3 py-2">
              <div className="flex min-w-0 items-center gap-2">
                <Badge variant={sevVariant(it.severity)}>{it.severity}</Badge>
                <span className="truncate text-sm font-medium text-[var(--color-text-primary)]">{it.appName}</span>
                <span className="whitespace-nowrap text-xs text-[var(--color-text-secondary)]">{CONDITION_LABEL[it.condition]}</span>
                <Badge variant="secondary" size="xs">{it.importanceTier}</Badge>
              </div>
              <Link to={it.jobActivityLink} className="flex shrink-0 items-center gap-1 text-xs text-[var(--color-accent)] hover:underline">
                View activity <ArrowRight size={12} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export default AttentionFeed;
